import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";

import { apiService } from "../app/api";
import BaseLayout from "../layout/BaseLayout";
import MultiSelectDropdown from "../component/MultiSelectDropdown";
import { toast } from "react-toastify";
import { handleAxiosError } from "../helper/errorHandler";
import { formatTimestamp } from "../helper/formatTimestamp";

interface TTSEmotionConfigType {
  _id: string;
  tts_emotion_model: string;
  languages: string[];
  emotion_tags: string[];
  avatars: string[];
  active: boolean;
  createdAt: string;
}

const MODEL_OPTIONS = ["narris-gamma", "narris-zeta"];

const TTSEmotionConfig: React.FC = () => {
  const [configs, setConfigs] = useState<TTSEmotionConfigType[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newModel, setNewModel] = useState("");
  const [newLanguages, setNewLanguages] = useState<string[]>([]);
  const [emotionInput, setEmotionInput] = useState("");
  const [avatarInput, setAvatarInput] = useState("");

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editModel, setEditModel] = useState("");
  const [editLanguages, setEditLanguages] = useState<string[]>([]);
  const [editEmotionInput, setEditEmotionInput] = useState("");
  const [editAvatarInput, setEditAvatarInput] = useState("");

  const parseEmotionTags = (input: string): string[] => {
    return input
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  };

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get("/tts-emotion-config");
      setConfigs(data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch configs");
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async () => {
    try {
      const { data } = await apiService.get(
        "/languages?is_active=true&page=1&limit=1000&fields=language"
      );

      const langArray = data.data.map((l: any) => l.language);
      setLanguages(langArray);
    } catch (error: any) {
      handleAxiosError(error);
    }
  };

  useEffect(() => {
    fetchConfigs();
    fetchLanguages();
  }, []);

  const handleAdd = async () => {
    const emotionArray = parseEmotionTags(emotionInput);
    const avatarArray = parseEmotionTags(avatarInput);

    if (!newModel) {
      toast.warning("Select model");
      return;
    }

    if (!newLanguages.length) {
      toast.warning("Select languages");
      return;
    }

    if (!emotionArray.length) {
      toast.warning("Enter emotion tags");
      return;
    }

    if (!avatarArray.length) {
      toast.warning("Enter avatars");
      return;
    }

    const payload = {
      tts_emotion_model: newModel,
      languages: newLanguages,
      avatars: avatarArray,
      emotion_tags: emotionArray,
    };

    try {
      const { data } = await apiService.post("/tts-emotion-config", payload);

      toast.success(data.message);

      setAddDialogOpen(false);
      setNewModel("");
      setNewLanguages([]);
      setEmotionInput("");
      setAvatarInput("");

      fetchConfigs();
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  const handleEditClick = (item: TTSEmotionConfigType) => {
    setEditId(item._id);
    setEditModel(item.tts_emotion_model);
    setEditLanguages(item.languages);
    setEditEmotionInput(item.emotion_tags.join(","));
    setEditAvatarInput(item.avatars?.join(","));
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    const emotionArray = parseEmotionTags(editEmotionInput);
    const avatarArray = parseEmotionTags(editAvatarInput);

    const payload = {
      languages: editLanguages,
      avatars: avatarArray,
      emotion_tags: emotionArray,
    };

    try {
      await apiService.put(`/tts-emotion-config/${editId}`, payload);

      toast.success("Updated successfully");

      setEditDialogOpen(false);

      fetchConfigs();
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  const toggleActive = async (id: string) => {
    try {
      await apiService.patch(`/toggle-tts-emotion-config-active/${id}`);
      fetchConfigs();
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  return (
    <BaseLayout>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize={30} fontWeight={700}>
          TTS Emotion Config ({configs.length})
        </Typography>

        <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
          Add Config
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <>
          {configs.length > 0 ? (
            <Grid container spacing={3} mt={3}>
              {configs.map((item) => (
                <Grid item xs={12} md={4} key={item._id}>
                  <Card sx={{ position: "relative", p: 1 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600}>
                        {item.tts_emotion_model}
                      </Typography>

                      <Typography variant="body2" color="textSecondary">
                        Added on: {formatTimestamp(item.createdAt)}
                      </Typography>

                      <Box mt={2}>
                        <Typography fontWeight={600}>Languages:</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {item.languages.map((lang) => (
                            <Chip key={lang} label={lang} size="small" />
                          ))}
                        </Box>
                      </Box>

                      <Box mt={2}>
                        <Typography fontWeight={600}>Emotion Tags:</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {item.emotion_tags.map((emotion) => (
                            <Chip
                              key={emotion}
                              label={emotion}
                              size="small"
                              color="secondary"
                            />
                          ))}
                        </Box>
                      </Box>

                      <Box mt={2}>
                        <Typography fontWeight={600}>Avatars:</Typography>

                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {item.avatars?.map((avatar) => (
                            <Chip
                              key={avatar}
                              label={avatar}
                              size="small"
                              color="primary"
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* <Box mt={2}>
                        <Chip
                          label={item.active ? "Active" : "Inactive"}
                          color={item.active ? "success" : "default"}
                        />
                      </Box> */}

                      {/* <IconButton
                        sx={{ position: "absolute", top: 8, right: 50 }}
                        onClick={() => toggleActive(item._id)}
                      >
                        {item.active ? (
                          <ToggleOnIcon color="success" />
                        ) : (
                          <ToggleOffIcon color="action" />
                        )}
                      </IconButton> */}

                      <IconButton
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        onClick={() => handleEditClick(item)}
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography textAlign="center" mt={5}>
              No Config Found
            </Typography>
          )}
        </>
      )}

      {/* ADD DIALOG */}

      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add TTS Emotion Config</DialogTitle>

        <DialogContent>
          <Typography mt={1} fontWeight={600}>
            Select Model
          </Typography>

          <select
            style={{ width: "100%", padding: 10, marginBottom: 20 }}
            value={newModel}
            onChange={(e) => setNewModel(e.target.value)}
          >
            <option value="">Select model</option>
            {MODEL_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          <MultiSelectDropdown
            options={languages}
            value={newLanguages}
            onChange={(v) => setNewLanguages(v)}
            label="Languages"
          />

          <Box mt={2} />

          <TextField
            label="Emotion Tags"
            placeholder="happy,sad,angry"
            helperText="Separate emotions with commas"
            multiline
            minRows={3}
            fullWidth
            value={emotionInput}
            onChange={(e) => setEmotionInput(e.target.value)}
          />

          <Box mt={2} />

          <TextField
            label="Avatars"
            placeholder="avatar1,avatar2,avatar3"
            helperText="Separate avatars with commas"
            multiline
            minRows={3}
            fullWidth
            value={avatarInput}
            onChange={(e) => setAvatarInput(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DIALOG */}

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit TTS Emotion Config</DialogTitle>

        <DialogContent>
          <TextField fullWidth disabled value={editModel} sx={{ mb: 2 }} />

          <MultiSelectDropdown
            options={languages}
            value={editLanguages}
            onChange={(v) => setEditLanguages(v)}
            label="Languages"
          />

          <Box mt={2} />

          <TextField
            label="Emotion Tags"
            multiline
            minRows={3}
            fullWidth
            value={editEmotionInput}
            onChange={(e) => setEditEmotionInput(e.target.value)}
          />
          <Box mt={2} />

          <TextField
            label="Avatars"
            multiline
            minRows={3}
            fullWidth
            value={editAvatarInput}
            onChange={(e) => setEditAvatarInput(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </BaseLayout>
  );
};

export default TTSEmotionConfig;
